<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="$t('adverts.MachineGroupConfig.213674-0')"
    width="900px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div v-loading="loading" class="config-container">
      <div class="left-section">
        <div class="section-header">
          <span>{{ $t("adverts.MachineGroupConfig.213674-1") }} ({{ selectedGroups.length }})</span>
          <el-button type="text" size="small" icon="el-icon-delete" @click="clearAll" v-if="selectedGroups.length > 0">
            {{ $t("adverts.MachineGroupConfig.213674-2") }}
          </el-button>
        </div>
        <div class="group-list">
          <div v-if="selectedGroups.length > 0">
            <div v-for="(group, index) in selectedGroups" :key="group.id" class="group-item selected">
              <div class="group-info">
                <span class="group-name">{{ group.name }}</span>
                <span class="group-count"
                  >{{ group.equipmentCount || 0 }} {{ $t("adverts.MachineGroupConfig.213674-3") }}</span
                >
              </div>
              <el-button type="text" icon="el-icon-close" @click="removeGroup(index)" />
            </div>
          </div>
          <el-empty v-else :description="$t('adverts.MachineGroupConfig.213674-4')" :image-size="80"></el-empty>
        </div>
      </div>

      <div class="right-section">
        <div class="section-header">
          <span>{{ $t("adverts.MachineGroupConfig.213674-5") }}</span>
          <el-input
            v-model="searchKeyword"
            :placeholder="$t('adverts.MachineGroupConfig.213674-6')"
            size="small"
            clearable
            style="width: 200px"
            @keyup.enter.native="loadMachineGroups"
          >
            <el-button slot="append" icon="el-icon-search" @click="loadMachineGroups"></el-button>
          </el-input>
        </div>
        <div class="group-list" v-loading="groupListLoading">
          <div v-if="availableGroups.length > 0">
            <div
              v-for="group in availableGroups"
              :key="group.id"
              class="group-item"
              :class="{ 'selected-in-list': isGroupSelected(group.id) }"
            >
              <div class="group-info">
                <span class="group-name">{{ group.name }}</span>
                <span class="group-count"
                  >{{ group.equipmentCount || 0 }} {{ $t("adverts.MachineGroupConfig.213674-3") }}</span
                >
              </div>
              <el-button
                v-if="isGroupSelected(group.id)"
                type="danger"
                size="mini"
                plain
                icon="el-icon-delete"
                @click="removeGroupById(group.id)"
              >
                {{ $t("advertGroupRela.remove") }}
              </el-button>
              <el-button v-else type="primary" size="mini" icon="el-icon-plus" @click="addGroup(group)">
                {{ $t("adverts.MachineGroupConfig.213674-8") }}
              </el-button>
            </div>
          </div>
          <el-empty v-else :description="$t('adverts.MachineGroupConfig.213674-9')"></el-empty>
        </div>
      </div>
    </div>

    <div slot="footer">
      <el-button @click="handleClose">{{ $t("adverts.MachineGroupConfig.213674-10") }}</el-button>
      <el-button type="primary" :loading="saving" @click="handleSave">{{
        $t("adverts.MachineGroupConfig.213674-11")
      }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
  export default {
    name: "MachineGroupConfig",
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      advertGroupId: {
        type: String,
        default: ""
      },
      apiNamespace: {
        type: String,
        default: "AdvertGroup"
      }
    },
    data() {
      return {
        dialogVisible: false,
        loading: false,
        saving: false,
        groupListLoading: false,
        searchKeyword: "",
        selectedGroups: [],
        availableGroups: []
      };
    },
    watch: {
      visible(val) {
        this.dialogVisible = val;
        if (val) {
          this.init();
        }
      },
      dialogVisible(val) {
        if (!val) {
          this.$emit("update:visible", false);
        }
      }
    },
    methods: {
      async init() {
        this.searchKeyword = "";
        this.selectedGroups = [];
        await this.loadSelectedGroups();
        await this.loadMachineGroups();
      },

      async loadSelectedGroups() {
        if (!this.advertGroupId) return;

        this.loading = true;
        try {
          const apiUrl =
            this.apiNamespace === "LabelAdvertGroup"
              ? this.$url[this.apiNamespace]?.getLabelGroupById
              : this.$url[this.apiNamespace]?.getEquipmentTeamById;
          const res = await this.$api.request(apiUrl, this.$method.get, {
            id: this.advertGroupId
          });
          if (res.code === 0 && res.data) {
            this.selectedGroups = Array.isArray(res.data) ? res.data : [];
          }
        } catch (error) {
          console.error(this.$t("adverts.MachineGroupConfig.213674-12"), error);
        } finally {
          this.loading = false;
        }
      },

      async loadMachineGroups() {
        this.groupListLoading = true;
        try {
          const params = {};
          if (this.searchKeyword) {
            params.name = this.searchKeyword;
          }

          let res;
          if (this.apiNamespace === "LabelAdvertGroup") {
            const pageParams = { ...params, currentPage: 1, pageSize: 1000 };
            res = await this.$api.request(this.$url.UserMachineLabels.page, this.$method.get, pageParams);
            if (res.code === 0 && res.data) {
              this.availableGroups = res.data.records || [];
            }
          } else {
            res = await this.$api.request(this.$url.UserMachineGroup.list, this.$method.get, params);
            if (res.code === 0) {
              this.availableGroups = res.data || [];
            }
          }
        } catch (error) {
          console.error(this.$t("adverts.MachineGroupConfig.213674-13"), error);
        } finally {
          this.groupListLoading = false;
        }
      },

      isGroupSelected(groupId) {
        return this.selectedGroups.some(g => g.id === groupId);
      },

      addGroup(group) {
        if (!this.isGroupSelected(group.id)) {
          this.selectedGroups.push({ ...group });
        }
      },

      removeGroup(index) {
        this.selectedGroups.splice(index, 1);
      },

      removeGroupById(groupId) {
        const index = this.selectedGroups.findIndex(group => group.id === groupId);
        if (index > -1) {
          this.removeGroup(index);
        }
      },

      clearAll() {
        this.$confirm(
          this.$t("adverts.MachineGroupConfig.213674-14"),
          this.$t("adverts.MachineGroupConfig.213674-15"),
          {
            confirmButtonText: this.$t("adverts.MachineGroupConfig.213674-16"),
            cancelButtonText: this.$t("adverts.MachineGroupConfig.213674-10"),
            type: "warning"
          }
        )
          .then(() => {
            this.selectedGroups = [];
          })
          .catch(() => {});
      },

      async handleSave() {
        if (!this.advertGroupId) {
          this.$message.error(this.$t("adverts.MachineGroupConfig.213674-17"));
          return;
        }

        this.saving = true;
        try {
          let data;
          if (this.apiNamespace === "LabelAdvertGroup") {
            data = {
              id: this.advertGroupId,
              labelGroupIds: this.selectedGroups.map(g => g.id)
            };
          } else {
            data = {
              id: this.advertGroupId,
              ids: this.selectedGroups.map(g => g.id)
            };
          }

          const apiUrl =
            this.apiNamespace === "LabelAdvertGroup"
              ? this.$url[this.apiNamespace]?.saveLabelGroup
              : this.$url[this.apiNamespace]?.teamConfig;
          const res = await this.$api.request(apiUrl, this.$method.put, data);

          if (res.code === 0) {
            this.$message.success(this.$t("adverts.MachineGroupConfig.213674-18"));
            this.$emit("success");
            this.handleClose();
          }
        } catch (error) {
          console.error(this.$t("adverts.MachineGroupConfig.213674-19"), error);
        } finally {
          this.saving = false;
        }
      },

      handleClose() {
        this.dialogVisible = false;
      }
    }
  };
</script>

<style scoped lang="scss">
  .config-container {
    display: flex;
    gap: 20px;
    min-height: 500px;
  }

  .left-section,
  .right-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #f5f7fa;
    border-radius: 4px;
    padding: 15px;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #dcdfe6;
    font-weight: 500;
    color: #303133;
  }

  .group-list {
    flex: 1;
    overflow-y: auto;
    max-height: 450px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #dcdfe6;
      border-radius: 3px;

      &:hover {
        background-color: #c0c4cc;
      }
    }
  }

  .group-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    margin-bottom: 10px;
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    transition: all 0.3s;

    &:hover {
      border-color: #409eff;
      box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
    }

    &.selected {
      border-color: #67c23a;
      background: #f0f9ff;

      .group-info .group-name {
        color: #409eff;
        font-weight: 500;
      }
    }

    &.selected-in-list {
      border-color: #f56c6c;
      background: #fef0f0;

      &:hover {
        border-color: #f56c6c;
        box-shadow: 0 2px 8px rgba(245, 108, 108, 0.12);
      }
    }

    .group-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
      overflow: hidden;
      margin-right: 10px;
    }

    .group-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #606266;
      font-size: 14px;
    }

    .group-count {
      font-size: 12px;
      color: #909399;
    }
  }
</style>
